/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable unused-imports/no-unused-vars */
import { styled } from '@mui/material';
import Typography from "../typography/typography";
import ReactPhoneInput, { CountryData } from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import { images } from "../../assets/images";
import { useLanguage } from '@erpsquad/common/runtime';
import { useCompanyCountryCode } from "../../hooks/useCompanyCountryCode";
import { memo, useEffect, useState } from "react";

const StyledInput = styled(ReactPhoneInput)<{ isRtl: boolean }>(({ theme: { palette }, isRtl }) => ({
  "&:hover": {
    border: "none",
  },
  "&.react-tel-input": {
    ".form-control": {
      borderRadius: "0.25rem",
      padding: "8px 8px 8px 55px", // Ensure space for flag on the left
      border: `1px solid ${palette.theme?.secondary[300]}`,
      fontSize: "0.875rem",
      width: "100%",
      maxHeight: "36.13px",
      direction: "ltr", // Force LTR direction for input to start from left
      textAlign: "left", // Force left alignment for input value

      ":hover": {
        border: `1px solid ${palette.theme?.secondary[400]}`,
      },

      ":focus": {
        border: `1px solid ${palette.theme?.primary[600]}`,
        boxShadow: "none",
      },
      ":disabled": {
        background: palette.theme?.secondary[200],
        color: palette.theme?.secondary[800],
      },

      "&.invalid-number": {
        border: `1px solid ${palette.theme?.error[600]}`,
        ":focus": {
          boxShadow: "none",
        },
      },
    },

    ".flag-dropdown": {
      left: "0",
      right: "auto",
      borderRadius: "0.25rem 0 0 0.25rem",

      ".selected-flag": {
        borderRadius: "0.25rem 0 0 0.25rem",

        "&:hover": {
          backgroundColor: palette.theme?.secondary[100],
        },
      },
    },

    ".country-list": {
      left:  "0",
      right: "auto",

      ".search": {
        padding: "0.5rem 0.5rem 0.5rem 0.75rem",
        borderBottom: "1px solid #F3F2F2",
        display: "flex",
        alignItems: "center",
        direction: isRtl ? "rtl" : "ltr",

        ".search-emoji": {
          position: "absolute",
          left: isRtl ? "auto" : "1.25rem",
          right: isRtl ? "1.25rem" : "auto",

          ":first-child": {
            display: "block",
            color: "transparent",
            background: `url(${images.search})`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
          },
        },

        ".search-box": {
          width: "100%",
          marginLeft: "0rem",
          border: `1px solid ${palette.theme?.secondary[200]}`,
          backgroundColor: palette.theme?.secondary[100],
          borderRadius: "0.5rem",
          padding: "0.4rem",
          paddingLeft: isRtl ? "0.4rem" : "2rem",
          paddingRight: isRtl ? "2rem" : "0.4rem",
          fontSize: "0.8125rem",
          direction: isRtl ? "rtl" : "ltr",
          textAlign: isRtl ? "right" : "left",

          ":hover": {
            border: `1px solid ${palette.theme?.secondary[300]}`,
          },

          ":focus": {
            border: `1px solid ${palette.theme?.primary[600]}`,
          },
        },
      },

      //
      // ".country": {
      //   fontSize: "0.8125rem",
      //   direction: isRtl ? "rtl" : "ltr",
      //   textAlign: isRtl ? "right" : "left",
      //
      //   ".dial-code": {
      //     fontSize: "0.75rem",
      //     position: "absolute",
      //     right: isRtl ? "auto" : "0.5rem",
      //     left: isRtl ? "0.5rem" : "auto",
      //   },
      // },
      //


      ".country": {
        fontSize: "0.8125rem",
        direction: isRtl ? "rtl" : "ltr",
        textAlign: isRtl ? "right" : "left",
        position: "relative",
        width: "100%",

        paddingLeft: isRtl ? "3rem" : "2.5rem",
        paddingRight: isRtl ? "3rem" : "0.5rem",

        ".dial-code": {
          fontSize: "0.75rem",
          position: "absolute",
          right: isRtl ? "auto" : "0.5rem",
          left: isRtl ? "0.5rem" : "auto",
        },

        ".flag": {
          position: "absolute",
          left: isRtl ? "auto" : "0.75rem",
          right: isRtl ? "0.75rem" : "auto",
        },
      },


    },
  },
}));

interface IPhoneInput {
  error?: boolean;
  name?: string;
  dataPosition?: number;
  dataName?: string;
  label: string;
  required?: boolean;
  placeholder?: string;
  default_value?: string;
  onChange?: (e: any) => void;
  disabled?: boolean;
  setCountryCodeField?: (country: CountryData) => void;
  helperText?: string;
  isInlineEditing?: boolean;
  country_code?: string;
}

function fixPhoneDropdown() {
  let dropdown, input;

  const positionDropdown = () => {
    if (!dropdown || !input) return;
    const rect = input.getBoundingClientRect();

    dropdown.style.position = "fixed";
    dropdown.style.top = rect.bottom + "px";
    dropdown.style.left = rect.left + "px";
    dropdown.style.zIndex = "9999";
  };

  document.addEventListener("click", () => {
    dropdown = document.querySelector(".react-tel-input .country-list");
    input = document.querySelector(".react-tel-input input");

    if (dropdown && input && !document.body.contains(dropdown)) {
      document.body.appendChild(dropdown);
    }

    positionDropdown();
  });

  // reposition on scroll (any container) and resize
  window.addEventListener("scroll", positionDropdown, true); // true = capture all scrolls
  window.addEventListener("resize", positionDropdown);
}


const HelperTextWrapper = styled('div')(() => ({
  marginTop: '0.4rem',
}));

const PhoneInput = memo(({
  name,
  error = false,
  dataName,
  dataPosition,
  label,
  required = false,
  placeholder,
  default_value,
  onChange,
  disabled,
  setCountryCodeField,
  helperText,
  isInlineEditing = false,
  country_code,
  ...rest
}: IPhoneInput) => {
  const { isRtl } = useLanguage();
  const { countryCode: dbCountryCode } = useCompanyCountryCode();

  const resolvedCountryCode = country_code || dbCountryCode;

  // Freeze country once a value is set to prevent react-phone-input-2's
  // componentDidUpdate → updateCountry() from discarding the number
  // when the async company country code resolves.
  const [stableCountry, setStableCountry] = useState(resolvedCountryCode);

  useEffect(() => {
    if (!rest?.value) {
      setStableCountry(resolvedCountryCode);
    }
  }, [resolvedCountryCode, rest?.value]);

  useEffect(() => {
    if (isInlineEditing) {
      fixPhoneDropdown(isInlineEditing)
    }
  }, [isInlineEditing])

  useEffect(() => {
    if (typeof setCountryCodeField === "function" && !country_code) {
      setCountryCodeField?.({ dialCode: "971" } as CountryData);
    }
  }, [country_code, dbCountryCode])

  return (
    <>
      <Typography
        type="s5"
        weight="medium"
        mb={0.5}
        color={error ? "theme.error.600" : "theme.secondary.800"}
        data-position={dataPosition}
        data-name={dataName}
      >
        {label} {required && <span className="requiredStar">*</span>}
      </Typography>
      <StyledInput
        dropdownContainer={document.body}
        isRtl={isRtl}
        specialLabel={""}
        enableSearch
        searchPlaceholder="Search Country"
        isValid={!error}
        country={stableCountry}
        searchNotFound="Not Found!"
        inputProps={{
          name: name,
          required: required,
        }}
        masks={{
          ae: '..........',
          sa: '..........'
        }}
        placeholder={placeholder}
        value={default_value || rest?.value}
        onChange={(value, country) => {
          setCountryCodeField?.(country as CountryData);
          onChange?.(value);
        }}
        disabled={disabled}
      />
      {helperText && (
        <HelperTextWrapper>
          <Typography
            type="s5"
            color={error ? "theme.error.600" : "theme.secondary.600"}
          >
            {helperText}
          </Typography>
        </HelperTextWrapper>
      )}
    </>
  );
});

export { PhoneInput };
export default PhoneInput;
